/**
 * Entity resolution — given a transaction's narration plus the household's
 * decrypted merchants, persons, and user-taught aliases, decide which
 * merchant or person (if any) the transaction belongs to.
 *
 * Runs entirely in the browser (or in MCP with a CryptoContext): the inputs
 * are decrypted lists, the output is an FK we can write back to the server.
 *
 * Resolution order:
 *   0. Exact UPI handle against `Person.upiHandles` (before any name match).
 *   1. Hardcoded global merchant aliases (the existing `MERCHANT_ALIASES`).
 *   2. User-taught merchant aliases for this household.
 *   3. User-taught person aliases for this household.
 *   4. fuzzball over decrypted merchants and persons (threshold 92).
 *
 * The parser-supplied `entityHint` narrows steps 2-4: when the hint is
 * "person" we try persons first; when "merchant", we try merchants first;
 * "unknown" runs both with merchants taking priority.
 */

import * as fuzz from "fuzzball"
import { resolveAlias } from "./aliases"
import { normaliseName } from "./normalise"

export type EntityHint = "merchant" | "person" | "unknown"

export interface MerchantAlias {
  pattern: string
  merchantId: string
}

export interface PersonAlias {
  pattern: string
  personId: string
}

export interface MerchantOption {
  id: string
  name: string
}

export interface PersonOption {
  id: string
  name: string
  /** Stored UPI handles (plaintext); matched exactly before any name matching. */
  upiHandles?: string[]
}

export interface ResolveContext {
  /** Plaintext global merchants the household can see. */
  globalMerchants: MerchantOption[]
  /** Decrypted local merchants for the household. */
  localMerchants: MerchantOption[]
  /** Decrypted persons for the household. */
  persons: PersonOption[]
  /** User-taught merchant aliases for this household. */
  userMerchantAliases: MerchantAlias[]
  /** User-taught person aliases for this household. */
  userPersonAliases: PersonAlias[]
}

export type ResolvedEntity =
  | { type: "merchant"; merchantId: string }
  | { type: "person"; personId: string }
  | null

/** fuzzball threshold for the decrypted-list fallback. */
const FUZZBALL_THRESHOLD = 92

// Cache compiled alias regexes for the lifetime of the module.
// Keyed by the lowercase pattern string; built once per unique pattern.
const _aliasReCache = new Map<string, RegExp>()

function getAliasRegex(lower: string): RegExp {
  let re = _aliasReCache.get(lower)
  if (!re) {
    // Escape metacharacters so user-supplied patterns don't cause ReDoS or syntax errors.
    const escaped = lower.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    re = new RegExp(`\\b${escaped}\\b`)
    _aliasReCache.set(lower, re)
  }
  return re
}

// Cache normalised names per merchant/person object to avoid re-computing
// normaliseName(opt.name) on every transaction in the inner fuzz-match loop.
const _normCache = new WeakMap<object, string>()

function cachedNormaliseName(opt: { name: string }): string {
  let cached = _normCache.get(opt)
  if (cached === undefined) {
    cached = normaliseName(opt.name)
    _normCache.set(opt, cached)
  }
  return cached
}

/**
 * Substring match against a list of patterns. Mirrors the matching style
 * used by `resolveAlias`: short patterns require word boundaries; longer
 * patterns just need to be contained.
 */
function matchUserAlias<T extends { pattern: string }>(
  normalisedName: string,
  aliases: T[],
): T | null {
  // Sort by pattern length descending so the most specific match wins.
  const sorted = [...aliases].sort((a, b) => b.pattern.length - a.pattern.length)
  for (const alias of sorted) {
    const lower = alias.pattern.toLowerCase()
    if (lower.length <= 4) {
      if (getAliasRegex(lower).test(normalisedName)) return alias
    } else if (normalisedName.includes(lower)) {
      return alias
    }
  }
  return null
}

function fuzzMatch<T extends { id: string; name: string }>(
  normalisedName: string,
  options: T[],
): T | null {
  let best: { row: T; score: number } | null = null
  for (const opt of options) {
    const candidate = cachedNormaliseName(opt)
    if (!candidate) continue
    const score = Math.max(
      fuzz.token_set_ratio(normalisedName, candidate),
      fuzz.ratio(normalisedName, candidate),
    )
    if (score >= FUZZBALL_THRESHOLD && (!best || score > best.score)) {
      best = { row: opt, score }
    }
  }
  return best?.row ?? null
}

function resolveMerchant(
  normalisedName: string,
  ctx: ResolveContext,
): { type: "merchant"; merchantId: string } | null {
  // 1. Hardcoded global aliases — return canonical name, then look up in
  //    globalMerchants by name.
  const canonical = resolveAlias(normalisedName)
  if (canonical) {
    const normCanonical = normaliseName(canonical)
    const match = ctx.globalMerchants.find((m) => cachedNormaliseName(m) === normCanonical)
    if (match) return { type: "merchant", merchantId: match.id }
  }

  // 2. User-taught merchant aliases (per household).
  const userAlias = matchUserAlias(normalisedName, ctx.userMerchantAliases)
  if (userAlias) return { type: "merchant", merchantId: userAlias.merchantId }

  // 3. fuzzball over both global + local merchants.
  const fuzzMerchant = fuzzMatch(normalisedName, [...ctx.globalMerchants, ...ctx.localMerchants])
  if (fuzzMerchant) return { type: "merchant", merchantId: fuzzMerchant.id }

  return null
}

function resolvePerson(
  normalisedName: string,
  ctx: ResolveContext,
): { type: "person"; personId: string } | null {
  // 1. User-taught person aliases.
  const userAlias = matchUserAlias(normalisedName, ctx.userPersonAliases)
  if (userAlias) return { type: "person", personId: userAlias.personId }

  // 2. fuzzball over decrypted persons.
  const fuzzPerson = fuzzMatch(normalisedName, ctx.persons)
  if (fuzzPerson) return { type: "person", personId: fuzzPerson.id }

  return null
}

/**
 * Exact (case-insensitive) UPI handle match against `Person.upiHandles`. Banks
 * truncate display names differently ("SUNITA GOALA" vs "SUNITA GOA") but the
 * handle in the narration is byte-identical, so it beats any name match. The
 * `@psp` suffix is ignored; the handle must be a whole token in the narration.
 */
export function resolvePersonByUpiHandle(
  description: string,
  persons: PersonOption[],
): { type: "person"; personId: string } | null {
  const text = description.toLowerCase()
  for (const p of persons) {
    for (const raw of p.upiHandles ?? []) {
      const handle = raw.toLowerCase().trim().split("@")[0]
      if (!handle) continue
      const escaped = handle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      if (new RegExp(`(^|[^a-z0-9._])${escaped}(@|[^a-z0-9._]|$)`).test(text)) {
        return { type: "person", personId: p.id }
      }
    }
  }
  return null
}

export function resolveEntity(name: string, hint: EntityHint, ctx: ResolveContext): ResolvedEntity {
  const byHandle = resolvePersonByUpiHandle(name, ctx.persons)
  if (byHandle) return byHandle
  const normalised = normaliseName(name)
  if (!normalised) return null

  if (hint === "person") {
    return resolvePerson(normalised, ctx) ?? resolveMerchant(normalised, ctx) ?? null
  }
  if (hint === "merchant") {
    return resolveMerchant(normalised, ctx) ?? null
  }
  // unknown -> merchant first, then person (mirrors today's default
  // behaviour where the engine assumes merchant unless told otherwise).
  return resolveMerchant(normalised, ctx) ?? resolvePerson(normalised, ctx) ?? null
}
